import React, { type ReactNode } from 'react';
import SectionMessage from '@atlaskit/section-message';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import type { AvailabilityContext } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/layout-items.tsx';
import { IPHLink } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/iph-link/index.tsx';
import { NewTabLink } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/new-tab-link/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { messages } from './messages.tsx';

export const NO_CONTEXT = 'NO_CONTEXT' as const;
const PRODUCT_HELP_ARTICLE_ID = '6I42ggwGORmP84atFaD3hS';
const PRODUCT_HELP_LINK =
	'https://support.atlassian.com/jira-cloud-administration/docs/configure-a-custom-field/#Troubleshoot-issue-configuration-and-context';

type HiddenInContextWarningProps = Readonly<{
	context: AvailabilityContext | typeof NO_CONTEXT;
	customFieldId: string;
	intl: IntlShape;
}>;

export const HiddenInContextWarning = ({
	context,
	customFieldId,
	intl,
}: HiddenInContextWarningProps) => {
	const link =
		context === NO_CONTEXT
			? `/secure/admin/ConfigureCustomField!default.jspa?customFieldId=${customFieldId}`
			: `/secure/admin/ManageConfigurationScheme!default.jspa?customFieldId=${customFieldId}&fieldConfigSchemeId=${context.id}`;

	return (
		<SectionMessage appearance="warning">
			{intl.formatMessage(
				fg('itsm-issue-renaming')
					? messages.contextNotAppliedIssueTermRefresh
					: messages.contextNotApplied,
				{
					a: (message: string[]): ReactNode => <NewTabLink href={link}>{message}</NewTabLink>,
					link: (message: string[]): ReactNode => (
						<IPHLink
							articleId={PRODUCT_HELP_ARTICLE_ID}
							documentationUrl={PRODUCT_HELP_LINK}
							linkText={{
								// Text that we get is already translated at this point and we're interested in the pure string only, so we only add this random id to comply with the API
								// and to make sure no real translation will be put under this fake key
								id: 'c29tZSBoYXNoIGZvciByZWFjdC1pbnRsIGtleQ1==.no-translate',
								defaultMessage: message[0],
							}}
						/>
					),
				},
			)}
		</SectionMessage>
	);
};
